import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface FilterState {
  search: string;
  status: string[];
  managerId?: string;
  tagIds?: number[];
  createdFrom?: string;
  createdTo?: string;
  sort: string;
  direction: 'asc' | 'desc';
  page: number;
  perPage: number;
}

const DEFAULT_FILTERS: FilterState = {
  search: '',
  status: [],
  managerId: undefined,
  tagIds: undefined,
  createdFrom: undefined,
  createdTo: undefined,
  sort: 'created_at',
  direction: 'desc',
  page: 1,
  perPage: 25,
};

export function useFilters(initialFilters: Partial<FilterState> = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFiltersState] = useState<FilterState>(() => {
    return {
      ...DEFAULT_FILTERS,
      ...initialFilters,
      ...parseFiltersFromURL(searchParams),
    };
  });

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.search) params.set('search', filters.search);
    if (filters.status.length > 0) {
      filters.status.forEach(s => params.append('status', s));
    }
    if (filters.managerId) params.set('manager_id', filters.managerId);
    if (filters.tagIds && filters.tagIds.length > 0) {
      filters.tagIds.forEach(id => params.append('tag_id', id.toString()));
    }
    if (filters.createdFrom) params.set('created_from', filters.createdFrom);
    if (filters.createdTo) params.set('created_to', filters.createdTo);
    if (filters.sort !== DEFAULT_FILTERS.sort) params.set('sort', filters.sort);
    if (filters.direction !== DEFAULT_FILTERS.direction) params.set('direction', filters.direction);
    if (filters.page !== DEFAULT_FILTERS.page) params.set('page', filters.page.toString());
    if (filters.perPage !== DEFAULT_FILTERS.perPage) params.set('per_page', filters.perPage.toString());

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const setFilters = useCallback((updates: Partial<FilterState>) => {
    setFiltersState(prev => ({
      ...prev,
      ...updates,
      // Reset to page 1 when filters change (except when changing page itself)
      page: updates.page !== undefined ? updates.page : 1,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  const setSearch = useCallback((search: string) => {
    setFilters({ search });
  }, [setFilters]);

  const setStatus = useCallback((status: string[]) => {
    setFilters({ status });
  }, [setFilters]);

  const setManagerId = useCallback((managerId: string | undefined) => {
    setFilters({ managerId });
  }, [setFilters]);

  const setTagIds = useCallback((tagIds: number[] | undefined) => {
    setFilters({ tagIds });
  }, [setFilters]);

  const setDateRange = useCallback((createdFrom?: string, createdTo?: string) => {
    setFilters({ createdFrom, createdTo });
  }, [setFilters]);

  const setSort = useCallback((sort: string, direction: 'asc' | 'desc' = 'desc') => {
    setFilters({ sort, direction });
  }, [setFilters]);

  const setPage = useCallback((page: number) => {
    setFiltersState(prev => ({ ...prev, page }));
  }, []);

  const hasActiveFilters =
    filters.search !== '' ||
    filters.status.length > 0 ||
    filters.managerId !== undefined ||
    (filters.tagIds !== undefined && filters.tagIds.length > 0) ||
    filters.createdFrom !== undefined ||
    filters.createdTo !== undefined;

  return {
    filters,
    setFilters,
    resetFilters,
    setSearch,
    setStatus,
    setManagerId,
    setTagIds,
    setDateRange,
    setSort,
    setPage,
    hasActiveFilters,
  };
}

function parseFiltersFromURL(params: URLSearchParams): Partial<FilterState> {
  const filters: Partial<FilterState> = {};

  const search = params.get('search');
  if (search) filters.search = search;

  const status = params.getAll('status');
  if (status.length > 0) filters.status = status;

  const managerId = params.get('manager_id');
  if (managerId) filters.managerId = managerId;

  const tagIds = params.getAll('tag_id');
  if (tagIds.length > 0) filters.tagIds = tagIds.map(id => parseInt(id, 10));

  const createdFrom = params.get('created_from');
  if (createdFrom) filters.createdFrom = createdFrom;

  const createdTo = params.get('created_to');
  if (createdTo) filters.createdTo = createdTo;

  const sort = params.get('sort');
  if (sort) filters.sort = sort;

  const direction = params.get('direction');
  if (direction === 'asc' || direction === 'desc') filters.direction = direction;

  const page = params.get('page');
  if (page) filters.page = parseInt(page, 10);

  const perPage = params.get('per_page');
  if (perPage) filters.perPage = parseInt(perPage, 10);

  return filters;
}
